import { Scene } from "phaser";
import i18next from "i18next";
import { generateUUID } from "../utils/uuid";
import { connectToServer } from "../network";
import { COLORS, hexToPhaser } from "../ui/colors";
import { handleSceneResize } from "../utils/camera-utils";
import { isTouchDevice } from "../utils/mobile-utils";
import { initVersionCheck, setCurrentVersion } from "../utils/version-check";
import { playSound } from "../utils/sound-utils";
import { ModernButton } from "../ui/modern-button";
import { trackEvent } from "../utils/ga-events";
import packageJson from "../../package.json" assert { type: "json" };

export class LoadingScene extends Scene {
  private static readonly VERSION = packageJson.version;
  private loadingText!: Phaser.GameObjects.Text;
  private versionText!: Phaser.GameObjects.Text;
  private connectionTimeout?: Phaser.Time.TimerEvent;
  private progressBar!: Phaser.GameObjects.Graphics;
  private progressBarBackground!: Phaser.GameObjects.Graphics;
  private progressBarBorder!: Phaser.GameObjects.Graphics;
  private logo!: Phaser.GameObjects.Image;
  private logoPortal!: Phaser.GameObjects.Image;
  private background!: Phaser.GameObjects.Image;
  private bubbleSquidLogo!: Phaser.GameObjects.Image;
  private dotCount: number = 0;
  private dotTimer?: Phaser.Time.TimerEvent;
  private progressTimer?: Phaser.Time.TimerEvent;
  private currentProgress: number = 0;
  private isProgressComplete: boolean = false;

  constructor() {
    super({ key: "LoadingScene" });
  }

  preload() {
    // Load the background image
    this.load.image("start-bg", "sprites/start-bg.png");
    // Load the logo parts
    this.load.image("logo-portal", "icons/logo-portal-only.png");
    this.load.image("logo-no-portal", "icons/logo-no-portal.png");
    this.load.image("bubble-squid-logo", "icons/bubble-squid-logo.png");
    // Load the close icon for error screen
    this.load.image("close-icon", "sprites/icons/close-icon.png");
    // Load the loading complete sound
    this.load.audio("loading-complete", "sfx/loading-complete.ogg");

    // Load UI sounds that are used globally across scenes
    this.load.audio("shop-sound", "/sfx/ui/shop.ogg");
    this.load.audio("open-container-sound", "/sfx/ui/open-container.ogg");
    this.load.audio("close-container-sound", "/sfx/ui/close-container.ogg");
    this.load.audio("inventory-open", "/sfx/ui/inventory-open.ogg");
    this.load.audio("pick-item-sound", "/sfx/ui/pick-item.ogg");
    this.load.audio("menu-click-sound", "/sfx/ui/menu-click.ogg");
    this.load.audio("highlight", "/sfx/ui/highlight.ogg");
    this.load.audio("step", "/sfx/step.ogg");
    this.load.audio("teleport", "/sfx/teleport.ogg");
    this.load.audio("extraction-success", "/sfx/extraction-success.ogg");
    this.load.audio("extraction-failed", "/sfx/extraction-failed.ogg");
    this.load.audio("base-fire", "/sfx/base-fire.ogg");
    this.load.audio("base-1", "/music/base-1.mp3");
    this.load.audio("base-2", "/music/base-2.mp3");
  }

  create() {
    const { width, height } = this.scale;

    // Add version number in the lower right corner
    this.versionText = this.add
      .text(width - 20, height - 20, LoadingScene.VERSION, {
        fontSize: "14px",
        color: "#ffffff",
        fontFamily: "monospace",
        stroke: "#000000",
        strokeThickness: 3,
      })
      .setOrigin(1, 1)
      .setDepth(1000);

    // Set current version for version checking
    setCurrentVersion(LoadingScene.VERSION);

    // Track session start
    trackEvent("session-start");

    // Initialize version checking system
    initVersionCheck();

    // Add the background image and center it
    this.background = this.add.image(width / 2, height / 2, "start-bg");
    this.background.setOrigin(0.5);
    this.background.setDisplaySize(width, height);

    this.cameras.main.setBackgroundColor("#181a20");
    // Move LoadingScene camera to render last (on top) by updating render order
    // Get the camera list and move this camera to the end
    const cameras = this.cameras.cameras;
    const loadingCamera = this.cameras.main;
    const index = cameras.indexOf(loadingCamera);
    if (index > -1) {
      cameras.splice(index, 1);
      cameras.push(loadingCamera);
    }

    // Game Logo with spinning portal (at the top-center)
    const logoSize = 180;
    const baseY = height / 2;
    const logoY = baseY - (isTouchDevice() ? 20 : 40);

    // Add spinning portal behind
    this.logoPortal = this.add.image(width / 2, logoY - 60, "logo-portal");
    this.logoPortal.setDisplaySize(logoSize * 0.8, logoSize * 0.8);

    // Add continuous rotation tween for the portal
    this.tweens.add({
      targets: this.logoPortal,
      angle: 360,
      duration: 3000,
      repeat: -1,
      ease: "Linear",
    });

    // Add main logo (no portal) on top
    this.logo = this.add.image(width / 2, logoY, "logo-no-portal");
    this.logo.setDisplaySize(logoSize, logoSize);
    this.logo.setOrigin(0.5);

    // Add bubble squid logo in the bottom left corner
    const bubbleSquidWidth = 324 / 3;
    const bubbleSquidHeight = 200 / 3;
    this.bubbleSquidLogo = this.add.image(
      10 + bubbleSquidWidth / 2,
      height - bubbleSquidHeight / 2,
      "bubble-squid-logo",
    );
    this.bubbleSquidLogo.setDisplaySize(bubbleSquidWidth, bubbleSquidHeight);
    this.bubbleSquidLogo.setOrigin(0.5);
    this.bubbleSquidLogo.setDepth(1000);

    // Main loading text with animated dots
    this.loadingText = this.add
      .text(width / 2, height / 2 + 140, i18next.t("scenes:loading.loading"), {
        fontSize: "24px",
        color: COLORS.WHITE,
        fontFamily: "Anton, sans-serif",
        stroke: "#000000",
        strokeThickness: 4,
      })
      .setOrigin(0.5);

    // Animate the dots
    this.dotTimer = this.time.addEvent({
      delay: 500,
      callback: this.updateLoadingDots,
      callbackScope: this,
      loop: true,
    });

    // Progress bar background
    const barWidth = 300;
    const barHeight = 20;
    const barX = width / 2 - barWidth / 2;
    const barY = height / 2 + 100;

    // Drop shadow offset below the bar
    this.progressBarBackground = this.add.graphics();
    this.progressBarBackground.fillStyle(0x000000, 0.5);
    this.progressBarBackground.fillRect(barX, barY + 4, barWidth, barHeight);

    // Progress bar fill
    this.progressBar = this.add.graphics();

    // Border drawn on top of both background and fill
    this.progressBarBorder = this.add.graphics();
    this.progressBarBorder.lineStyle(2, 0xffffff, 1);
    this.progressBarBorder.strokeRect(barX, barY, barWidth, barHeight);

    // Start progress animation (1 minute = 60000ms)
    this.progressTimer = this.time.addEvent({
      delay: 60000 / 100, // Update 100 times over 60 seconds
      callback: this.updateProgress,
      callbackScope: this,
      repeat: 99,
    });

    // Listen for resize events to maintain camera zoom
    this.scale.on("resize", this.handleResize, this);

    // Unlock audio context on iOS by resuming it immediately
    // This will be triggered by user's first touch but we try to resume early
    const soundManager = this.sound as Phaser.Sound.WebAudioSoundManager;
    if (soundManager.context && soundManager.context.state === "suspended") {
      soundManager.context.resume().then(() => {
        this.tryPlayLoadingSound();
      });
    } else {
      this.tryPlayLoadingSound();
    }

    this.connectAsGuest();
  }

  private tryPlayLoadingSound() {
    // Try to play loading complete sound
    try {
      playSound(this, "loading-complete");
    } catch (error) {
      console.error("Error playing loading complete sound:", error);
    }
  }

  private updateLoadingDots() {
    this.dotCount = (this.dotCount + 1) % 4;
    const dots = ".".repeat(this.dotCount);
    this.loadingText.setText(i18next.t("scenes:loading.loading") + dots);
  }

  private updateProgress() {
    this.currentProgress += 1;
    const { width, height } = this.scale;
    const barWidth = 300;
    const barHeight = 18;
    const barX = width / 2 - barWidth / 2;
    const barY = height / 2 + 100;

    this.progressBar.clear();
    this.progressBar.fillStyle(hexToPhaser(COLORS.GREEN), 1);
    this.progressBar.fillRect(
      barX,
      barY,
      (barWidth * this.currentProgress) / 100,
      barHeight,
    );

    // When complete, start blinking
    if (this.currentProgress >= 100 && !this.isProgressComplete) {
      this.isProgressComplete = true;
      this.startProgressBarBlink();
    }
  }

  private startProgressBarBlink() {
    this.tweens.add({
      targets: this.progressBar,
      alpha: 0.3,
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });
  }

  private handleResize = (gameSize: Phaser.Structs.Size) => {
    handleSceneResize(this, gameSize);

    const { width, height } = gameSize;

    // Update version text position on resize
    if (this.versionText) {
      this.versionText.setPosition(width - 20, height - 20);
    }

    // Update bubble squid logo position
    if (this.bubbleSquidLogo) {
      const bubbleSquidSize = 80;
      this.bubbleSquidLogo.setPosition(
        20 + bubbleSquidSize / 2,
        height - 20 - bubbleSquidSize / 2,
      );
    }

    // Resize and reposition background
    if (this.background) {
      this.background.setPosition(width / 2, height / 2);
      this.background.setDisplaySize(width, height);
    }

    // Update logo positions
    const baseY = height / 2;
    const logoY = baseY - (isTouchDevice() ? 20 : 40);

    if (this.logoPortal) {
      this.logoPortal.setPosition(width / 2, logoY - 60);
    }

    if (this.logo) {
      this.logo.setPosition(width / 2, logoY);
    }

    // Update loading text position
    if (this.loadingText) {
      this.loadingText.setPosition(width / 2, height / 2 + 140);
    }

    // Update progress bar position
    const barWidth = 300;
    const barHeight = 20;
    const barX = width / 2 - barWidth / 2;
    const barY = height / 2 + 100;

    if (this.progressBarBackground) {
      this.progressBarBackground.clear();
      this.progressBarBackground.fillStyle(0x000000, 0.5);
      this.progressBarBackground.fillRect(barX, barY + 4, barWidth, barHeight);
    }

    if (this.progressBar) {
      this.progressBar.clear();
      this.progressBar.fillStyle(hexToPhaser(COLORS.GREEN), 1);
      this.progressBar.fillRect(
        barX,
        barY,
        (barWidth * this.currentProgress) / 100,
        barHeight,
      );
    }

    if (this.progressBarBorder) {
      this.progressBarBorder.clear();
      this.progressBarBorder.lineStyle(2, 0xffffff, 1);
      this.progressBarBorder.strokeRect(barX, barY, barWidth, barHeight);
    }
  };

  private async connectAsGuest() {
    const GUEST_TOKEN_KEY = "guestToken";
    let guestToken = localStorage.getItem(GUEST_TOKEN_KEY);
    if (!guestToken) {
      guestToken = generateUUID();
      localStorage.setItem(GUEST_TOKEN_KEY, guestToken);
    }

    // Check internet connection first
    const hasInternet = await this.checkInternetConnection();
    if (!hasInternet) {
      console.error("No internet connection");
      this.showNoInternetError();
      return;
    }

    // Set up a timeout for server connection (15 seconds)
    const connectionTimeoutMs = 15000;
    let connectionTimedOut = false;

    this.connectionTimeout = this.time.addEvent({
      delay: connectionTimeoutMs,
      callback: async () => {
        if (!connectionTimedOut && window.socket && !window.socket.connected) {
          connectionTimedOut = true;
          console.error(
            "Connection timeout: Server did not respond within 15 seconds",
          );
          // Check if internet was lost
          const hasInternet = await this.checkInternetConnection();
          if (!hasInternet) {
            this.showNoInternetError();
          } else {
            this.showConnectionError();
          }
        }
      },
      callbackScope: this,
    });

    const isConnected = await connectToServer();
    if (!isConnected) {
      console.error("Failed to connect to server.");
      if (!connectionTimedOut) {
        // Check if internet was lost
        const hasInternet = await this.checkInternetConnection();
        if (!hasInternet) {
          this.showNoInternetError();
        } else {
          this.showConnectionError();
        }
      }
      return;
    }

    // Connection successful - cancel the timeout if still pending
    if (this.connectionTimeout) {
      this.connectionTimeout.destroy();
      this.connectionTimeout = undefined;
    }

    // Connection successful - update status and complete loading bar animation
    // Stop the dot animation and update text
    if (this.dotTimer) {
      this.dotTimer.destroy();
    }
    this.loadingText.setText(i18next.t("scenes:loading.connectedToServer"));
    this.completeLoadingBarAnimation();

    if (window.socket) {
      window.socket.on(
        "play-guest-success",
        (data: {
          accountId: number;
          isFirstTime: boolean;
          accountName?: string;
          reconnectToMatch?: {
            position: { x: number; y: number };
            health: number;
            maxHealth: number;
            armor?: number;
            maxArmor?: number;
            matchTimeRemaining?: number;
          };
          pendingMatchResults?: any;
        }) => {
          // Store accountId globally for use throughout the app
          (window as any).accountId = data.accountId;

          // Check if there are pending match results (died while disconnected)
          if (data.pendingMatchResults) {
            this.loadingText.setText(i18next.t("scenes:loading.loadingMatchResults"));

            // Wait for loading bar to complete before showing results
            const checkComplete = this.time.addEvent({
              delay: 100,
              callback: () => {
                if (this.isProgressComplete) {
                  checkComplete.destroy();
                  // Add 1 second delay after progress bar is complete
                  this.time.delayedCall(1000, () => {
                    // Store the pending results globally so BaseScene can show them
                    (window as any).pendingMatchResults =
                      data.pendingMatchResults;

                    // Launch BaseScene alongside LoadingScene with overlay
                    this.scene.launch("BaseScene", {
                      showLoadingOverlay: true,
                      onSceneReady: () => this.fadeOutAndStop(),
                    });
                  });
                }
              },
              callbackScope: this,
              loop: true,
            });
            return;
          }

          // Check if reconnecting to active match
          if (data.reconnectToMatch) {
            this.loadingText.setText(i18next.t("scenes:loading.reconnectingToMatch"));

            // Set match duration for timer initialization
            if (data.reconnectToMatch.matchTimeRemaining) {
              (window as any).matchDuration =
                data.reconnectToMatch.matchTimeRemaining;
            }

            // Set the map for reconnection
            if (data.reconnectToMatch.selectedMap) {
              (window as any).selectedMap = data.reconnectToMatch.selectedMap;
            }

            // Wait for loading bar to complete before transitioning to match
            const checkComplete = this.time.addEvent({
              delay: 100,
              callback: () => {
                if (this.isProgressComplete) {
                  checkComplete.destroy();
                  // Add 1 second delay after progress bar is complete
                  this.time.delayedCall(1000, () => {
                    // Check if MainScene exists, if not add it, then start it
                    const existingMainScene = this.scene.get("MainScene");
                    if (!existingMainScene) {
                      // Import MainScene dynamically and add it
                      import("./main-scene").then(({ MainScene }) => {
                        this.scene.add("MainScene", MainScene, false);
                        this.scene.start("MainScene", {
                          isReconnecting: true,
                          reconnectionData: data.reconnectToMatch,
                        });
                      });
                    } else {
                      this.scene.start("MainScene", {
                        isReconnecting: true,
                        reconnectionData: data.reconnectToMatch,
                      });
                    }
                  });
                }
              },
              callbackScope: this,
              loop: true,
            });
            return;
          }

          // Normal login flow - update loading text with account name if available
          if (data.accountName && !data.isFirstTime) {
            this.loadingText.setText(i18next.t("scenes:loading.loggedInAs", { playerName: data.accountName }));
            trackEvent("login-success", { accountName: data.accountName });
          }else {
            trackEvent("login-success", { accountName: "first time!" });        
          }

          // Wait for loading bar to complete before transitioning
          const checkComplete = this.time.addEvent({
            delay: 100,
            callback: () => {
              if (this.isProgressComplete) {
                checkComplete.destroy();
                // Add 1 second delay after progress bar is complete
                this.time.delayedCall(1000, () => {
                  this.transitionToNextScene(data);
                });
              }
            },
            callbackScope: this,
            loop: true,
          });
        },
      );

      window.socket.emit("play-guest", { guestToken });
    }
  }

  private transitionToNextScene(data: {
    accountId: number;
    isFirstTime: boolean;
  }) {
    // Launch BaseScene alongside LoadingScene (no black screen)
    // LoadingScene will fade out once BaseScene is ready
    this.scene.launch("BaseScene", {
      showLoadingOverlay: true,
      onSceneReady: () => this.fadeOutAndStop(),
    });

    // Move LoadingScene camera to render on top after launching BaseScene
    this.time.delayedCall(10, () => {
      const cameras = this.cameras.cameras;
      const loadingCamera = this.cameras.main;
      const index = cameras.indexOf(loadingCamera);
      if (index > -1) {
        cameras.splice(index, 1);
        cameras.push(loadingCamera);
      }
    });
  }

  private fadeOutAndStop() {
    // Fade out loading scene over 500ms
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 0,
      duration: 500,
      ease: "Quad.easeInOut",
      onComplete: () => {
        // Sleep the loading scene (suspended but can be restarted if needed)
        this.scene.sleep("LoadingScene");
      },
    });

    // Fade in BaseScene camera as loading scene fades out
    const baseScene = this.scene.get("BaseScene");
    if (baseScene) {
      this.tweens.add({
        targets: baseScene.cameras.main,
        alpha: 1,
        duration: 500,
        ease: "Quad.easeInOut",
      });
    }
  }

  private completeLoadingBarAnimation() {
    // Stop the slow progress timer
    if (this.progressTimer) {
      this.progressTimer.destroy();
    }

    // Calculate remaining progress and animate it quickly
    const remainingProgress = 100 - this.currentProgress;
    if (remainingProgress <= 0) {
      // Already at 100%, mark as complete
      this.isProgressComplete = true;
      return;
    }

    // Animate remaining progress over 200ms
    const animationDuration = 200; // 200ms
    const updateInterval = 30; // Update every 30ms for smooth animation
    const totalUpdates = animationDuration / updateInterval;
    const incrementPerUpdate = remainingProgress / totalUpdates;

    this.progressTimer = this.time.addEvent({
      delay: updateInterval,
      callback: () => {
        this.currentProgress = Math.min(
          100,
          this.currentProgress + incrementPerUpdate,
        );
        this.updateProgress();
      },
      callbackScope: this,
      repeat: totalUpdates - 1,
    });
  }

  private showConnectionError() {
    // Stop timers
    if (this.dotTimer) {
      this.dotTimer.destroy();
    }
    if (this.progressTimer) {
      this.progressTimer.destroy();
    }

    // Hide the spinning portal logo and main logo
    if (this.logoPortal) {
      this.logoPortal.setVisible(false);
    }
    if (this.logo) {
      this.logo.setVisible(false);
    }

    // Clear existing text and elements (keep background and logos)
    this.children.list
      .filter(
        (child) =>
          !(
            child === this.background ||
            child === this.logo ||
            child === this.logoPortal ||
            child === this.bubbleSquidLogo ||
            child === this.versionText
          ),
      )
      .forEach((child) => (child as any).destroy());

    const { width, height } = this.scale;

    // Black circle behind the close icon
    const circleGraphics = this.add.graphics();
    circleGraphics.fillStyle(0x000000, 1);
    circleGraphics.fillCircle(width / 2, height / 2 - 100, 35);

    // White close icon
    const closeIcon = this.add.image(width / 2, height / 2 - 100, "close-icon");
    closeIcon.setDisplaySize(50, 50);

    // Error title with hard shadow
    this.add
      .text(width / 2, height / 2 - 40, i18next.t("scenes:loading.cantConnect.title"), {
        fontSize: "28px",
        color: "#ffffff",
        fontFamily: "Anton, sans-serif",
        stroke: "#000000",
        strokeThickness: 4,
        shadow: {
          offsetX: 0,
          offsetY: 4,
          color: "#000000",
          blur: 0,
          stroke: true,
          fill: true,
        },
      })
      .setOrigin(0.5);

    // Error message with hard shadow

    this.add
      .text(width / 2, height / 2 + 35, i18next.t("scenes:loading.cantConnect.message"), {
        fontSize: "18px",
        color: "#ffffff",
        fontFamily: "Anton, sans-serif",
        stroke: "#000000",
        strokeThickness: 3,
        shadow: {
          offsetX: 0,
          offsetY: 3,
          color: "#000000",
          blur: 0,
          stroke: true,
          fill: true,
        },
      })
      .setOrigin(0.5);

    // Retry button using ModernButton
    new ModernButton({
      scene: this,
      x: width / 2,
      y: height / 2 + 80,
      text: i18next.t("scenes:loading.cantConnect.retry"),
      style: "success",
      onClick: () => {
        // Reload the page to retry connection
        window.location.reload();
      },
    });

    // Report button using ModernButton
    new ModernButton({
      scene: this,
      x: width / 2,
      y: height / 2 + 130,
      text: i18next.t("scenes:loading.cantConnect.report"),
      style: "danger",
      onClick: () => {
        // Open browser with report URL
        window.open(
          "https://bsky.app/profile/extractionzero.bsky.social",
          "_blank",
        );
      },
    });
  }

  private showNoInternetError() {
    // Stop timers
    if (this.dotTimer) {
      this.dotTimer.destroy();
    }
    if (this.progressTimer) {
      this.progressTimer.destroy();
    }

    // Hide the spinning portal logo and main logo
    if (this.logoPortal) {
      this.logoPortal.setVisible(false);
    }
    if (this.logo) {
      this.logo.setVisible(false);
    }

    // Clear existing text and elements (keep background and logos)
    this.children.list
      .filter(
        (child) =>
          !(
            child === this.background ||
            child === this.logo ||
            child === this.logoPortal ||
            child === this.bubbleSquidLogo ||
            child === this.versionText
          ),
      )
      .forEach((child) => (child as any).destroy());

    const { width, height } = this.scale;

    // Black circle behind the close icon
    const circleGraphics = this.add.graphics();
    circleGraphics.fillStyle(0x000000, 1);
    circleGraphics.fillCircle(width / 2, height / 2 - 100, 35);

    // White close icon
    const closeIcon = this.add.image(width / 2, height / 2 - 100, "close-icon");
    closeIcon.setDisplaySize(50, 50);

    // Error title with hard shadow
    this.add
      .text(width / 2, height / 2 - 40, i18next.t("scenes:noInternet.title"), {
        fontSize: "28px",
        color: "#ffffff",
        fontFamily: "Anton, sans-serif",
        stroke: "#000000",
        strokeThickness: 4,
        shadow: {
          offsetX: 0,
          offsetY: 4,
          color: "#000000",
          blur: 0,
          stroke: true,
          fill: true,
        },
      })
      .setOrigin(0.5);

    // Error message with hard shadow
    this.add
      .text(width / 2, height / 2 + 10, i18next.t("scenes:noInternet.message"), {
        fontSize: "18px",
        color: "#ffffff",
        fontFamily: "Anton, sans-serif",
        stroke: "#000000",
        strokeThickness: 3,
        shadow: {
          offsetX: 0,
          offsetY: 3,
          color: "#000000",
          blur: 0,
          stroke: true,
          fill: true,
        },
      })
      .setOrigin(0.5);

    // Retry button using ModernButton
    new ModernButton({
      scene: this,
      x: width / 2,
      y: height / 2 + 80,
      text: i18next.t("scenes:noInternet.retry"),
      style: "success",
      onClick: () => {
        // Reload the page to retry connection
        window.location.reload();
      },
    });
  }

  destroy() {
    this.scale.off("resize", this.handleResize, this);
    window.socket.off("play-guest-success");
    if (this.connectionTimeout) {
      this.connectionTimeout.destroy();
    }
    if (this.dotTimer) {
      this.dotTimer.destroy();
    }
    if (this.progressTimer) {
      this.progressTimer.destroy();
    }
    if (this.loadingText) {
      this.loadingText.destroy();
    }
    if (this.versionText) {
      this.versionText.destroy();
    }
    if (this.progressBar) {
      this.progressBar.destroy();
    }
    if (this.progressBarBackground) {
      this.progressBarBackground.destroy();
    }
    if (this.progressBarBorder) {
      this.progressBarBorder.destroy();
    }
    if (this.logo) {
      this.logo.destroy();
    }
    if (this.logoPortal) {
      this.logoPortal.destroy();
    }
    if (this.background) {
      this.background.destroy();
    }
    if (this.bubbleSquidLogo) {
      this.bubbleSquidLogo.destroy();
    }
  }

  private async checkInternetConnection(): Promise<boolean> {
    const serverUrl =
      (import.meta.env as any).VITE_SERVER_URL ||
      "https://mobile-extraction-server-eu.onrender.com";
    try {
      await fetch(serverUrl, {
        method: "HEAD",
        mode: "no-cors",
      });
      console.info("Internet connection verified");
      return true;
    } catch (error) {
      console.warn("Internet connection check failed:", error);
      return false;
    }
  }
}
